import type { RescheduleSuggestionDTO } from 'src/types/dto/reschedule-suggestion.dto';
import type { ActivityDTO } from 'src/types/dto/activity.dto';

import { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Button, Collapse, Stack, Typography } from '@mui/material';

import { Iconify } from 'src/components/iconify';
import { Label } from 'src/components/label';
import { TagsList } from 'src/components/tags-list';
import { UserIcon } from 'src/components/user-icon';
import { InspectorAvailabilityGridSkeletonLoadingRow } from 'src/components/shuffle/inspector-availability-grid';
import { getAuthorizationName } from 'src/utils/competency';
import { InspectorType } from 'src/types/inspector';
import { varAlpha } from 'src/theme/styles';

import { InspectorRescheduleCardChangeDetails } from './inspector-reschedule-card-change-details';
import {
  formatRescheduleCardDate,
  getAggregatedNewAppointments,
  getMainActivityProposedAppointment,
  getInspectorBusinessUnit,
  getActivityCompetencyIds,
  getInspectorAuthorizationsForActivity,
  getInspectorLocation,
  getTotalNewAppointmentDays,
  getTravelSummary,
  isSameTeamSuggestion,
} from './utils';

const COMPETENCIES_COLUMN_MAX_WIDTH = 280;

const sectionBgSx = {
  bgcolor: (theme: { vars: { palette: { grey: { '500Channel': string } } } }) =>
    varAlpha(theme.vars.palette.grey['500Channel'], 0.04),
};

const headerCellSx = {
  px: 2,
  height: 32,
  display: 'flex',
  alignItems: 'center',
  typography: 'body2',
  fontWeight: 600,
  color: 'text.secondary',
};

const bodyCellSx = {
  px: 2,
  py: 1,
  display: 'flex',
  alignItems: 'center',
  minHeight: 60,
};

export type InspectorRescheduleCardProps = {
  suggestion: RescheduleSuggestionDTO;
  currentActivity: ActivityDTO;
  isLoading?: boolean;
  isDeskService?: boolean;
  onSelect?: (suggestion: RescheduleSuggestionDTO) => void;
  siteTimezoneId?: string;
  onDismiss?: () => void;
};

export const InspectorRescheduleCard = memo(
  ({
    suggestion,
    currentActivity,
    isLoading = false,
    isDeskService = false,
    onSelect,
    siteTimezoneId,
    onDismiss,
  }: InspectorRescheduleCardProps) => {
    const { t, i18n } = useTranslation('shuffle');
    const [expanded, setExpanded] = useState(false);

    const { inspector, solutionId, changes } = suggestion;

    const mainProposedAppointment = useMemo(
      () => getMainActivityProposedAppointment(changes, currentActivity.id),
      [changes, currentActivity.id]
    );
    const aggregatedAppointments = useMemo(() => getAggregatedNewAppointments(changes), [changes]);
    const travelSummary = useMemo(
      () => getTravelSummary(aggregatedAppointments, isDeskService),
      [aggregatedAppointments, isDeskService]
    );
    const activityCompetencyIds = useMemo(
      () => getActivityCompetencyIds(currentActivity),
      [currentActivity]
    );
    const authorizationTags = useMemo(
      () =>
        getInspectorAuthorizationsForActivity(inspector, activityCompetencyIds).map(
          (authorization) => ({
            id: authorization.id,
            label: getAuthorizationName(authorization, i18n),
            color: 'info' as const,
            variant: 'filled' as const,
          })
        ),
      [activityCompetencyIds, i18n, inspector]
    );
    const showSameTeamBadge = useMemo(
      () => isSameTeamSuggestion(changes, inspector),
      [changes, inspector]
    );

    const handleSelect = useCallback(() => {
      onSelect?.(suggestion);
    }, [onSelect, suggestion]);

    const toggleExpanded = useCallback(() => {
      setExpanded((current) => !current);
    }, []);

    if (isLoading) {
      return (
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            overflow: 'hidden',
            bgcolor: 'background.paper',
          }}
        >
          <InspectorAvailabilityGridSkeletonLoadingRow opacity={0.08} />
        </Box>
      );
    }

    return (
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          overflow: 'hidden',
          bgcolor: 'background.paper',
        }}
      >
        <Box
          sx={{
            ...sectionBgSx,
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            gap: 2,
            p: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {showSameTeamBadge ? (
              <Label
                variant="soft"
                color="primary"
                startIcon={<Iconify icon="solar:users-group-rounded-bold" width={16} />}
              >
                {t('inspectorRescheduleCard.badges.sameTeam')}
              </Label>
            ) : null}
            {inspector.type ? (
              <Label
                variant="soft"
                color="secondary"
                startIcon={
                  <Iconify
                    icon={
                      inspector.type === InspectorType.EXTERNAL
                        ? 'solar:user-rounded-bold'
                        : 'solar:user-check-rounded-bold'
                    }
                    width={16}
                  />
                }
              >
                {inspector.type === InspectorType.EXTERNAL
                  ? t('inspectorRescheduleCard.badges.external')
                  : t('inspectorRescheduleCard.badges.internal')}
              </Label>
            ) : null}
          </Stack>

          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="h5">
              {formatRescheduleCardDate(mainProposedAppointment?.from)}
            </Typography>
            <Button
              variant="contained"
              size="small"
              endIcon={<Iconify icon="eva:arrow-forward-fill" width={18} />}
              onClick={handleSelect}
              sx={{ minWidth: 64, height: 30 }}
            >
              {t('inspectorRescheduleCard.select')}
            </Button>
          </Stack>
        </Box>

        <Box
          sx={{
            ...sectionBgSx,
            display: 'flex',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box sx={{ ...headerCellSx, width: 240, flexShrink: 0 }}>
            {t('inspectorRescheduleCard.columns.name')}
          </Box>
          <Box
            sx={{
              ...headerCellSx,
              flex: 1,
              minWidth: 0,
              maxWidth: COMPETENCIES_COLUMN_MAX_WIDTH,
            }}
          >
            {t('inspectorRescheduleCard.columns.competencies')}
          </Box>
          <Box sx={{ ...headerCellSx, width: 80, flexShrink: 0 }}>
            {t('inspectorRescheduleCard.columns.days')}
          </Box>
          <Box sx={{ ...headerCellSx, width: 80, flexShrink: 0 }}>
            {t('inspectorRescheduleCard.columns.businessUnit')}
          </Box>
          <Box sx={{ ...headerCellSx, width: 120, flexShrink: 0 }}>
            {t('inspectorRescheduleCard.columns.travel')}
          </Box>
        </Box>

        <Box
          sx={{
            ...sectionBgSx,
            display: 'flex',
            alignItems: 'stretch',
            borderBottom: expanded ? '1px solid' : 'none',
            borderColor: 'divider',
          }}
        >
          <Box sx={{ ...bodyCellSx, width: 240, maxWidth: 240, flexShrink: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
              <UserIcon id={inspector.id} name={inspector.name} type={inspector.type} />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" noWrap>
                  {inspector.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" noWrap>
                  {getInspectorLocation(inspector)}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              ...bodyCellSx,
              flex: 1,
              minWidth: 0,
              maxWidth: COMPETENCIES_COLUMN_MAX_WIDTH,
              alignItems: 'flex-start',
              py: 1,
              overflow: 'hidden',
            }}
          >
            <Box
              id={`inspector-reschedule-card-competencies-${solutionId}`}
              sx={{ width: '100%', minWidth: 0, minHeight: 24, maxHeight: 24 }}
            >
              <TagsList
                containerIdentifier={`inspector-reschedule-card-competencies-${solutionId}`}
                tags={authorizationTags}
                color="info"
                variant="filled"
                showTooltip
              />
            </Box>
          </Box>

          <Box sx={{ ...bodyCellSx, width: 80, maxWidth: 80, flexShrink: 0 }}>
            <Typography variant="body2">
              {getTotalNewAppointmentDays(changes, currentActivity.id)}
            </Typography>
          </Box>

          <Box sx={{ ...bodyCellSx, width: 80, maxWidth: 80, flexShrink: 0 }}>
            <Typography variant="body2" noWrap>
              {getInspectorBusinessUnit(inspector)}
            </Typography>
          </Box>

          <Box sx={{ ...bodyCellSx, width: 120, maxWidth: 120, flexShrink: 0 }}>
            <Box>
              <Typography variant="body2">{travelSummary.durationLabel}</Typography>
              {travelSummary.distanceLabel ? (
                <Typography variant="caption" color="text.secondary">
                  {travelSummary.distanceLabel}
                </Typography>
              ) : null}
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            ...sectionBgSx,
            borderTop: expanded ? '1px solid' : 'none',
            borderColor: 'divider',
            px: 2,
            py: 0.25,
          }}
        >
          <Button
            variant="text"
            size="small"
            onClick={toggleExpanded}
            endIcon={
              <Iconify
                icon={expanded ? 'eva:arrow-ios-upward-fill' : 'eva:arrow-ios-downward-fill'}
                width={18}
              />
            }
            sx={{ height: 30, px: 0.5 }}
          >
            {expanded
              ? t('inspectorRescheduleCard.hideDetails')
              : t('inspectorRescheduleCard.showDetails')}
          </Button>
        </Box>

        <Collapse in={expanded}>
          <Stack spacing={2} sx={{ ...sectionBgSx, p: 2, pt: 0 }}>
            {changes.map((change) => (
              <InspectorRescheduleCardChangeDetails
                key={`${solutionId}-${change.activity.id}`}
                change={change}
                proposedInspector={inspector}
                isDeskService={isDeskService}
                siteTimezoneId={siteTimezoneId}
                onDismiss={onDismiss}
              />
            ))}
          </Stack>
        </Collapse>
      </Box>
    );
  }
);

InspectorRescheduleCard.displayName = 'InspectorRescheduleCard';
